// Sheet stock step — used by cut_blank + hybrid parts.
//
// Captures sheet dimensions, thickness, material, rough nesting (qty per
// sheet), and a waste-percent fudge factor. Sheet/qty/waste are local-only
// today (no first-class fields on MaterialCost); material name + sheet
// price ride on materialCost the same way the machined MaterialStep handles
// them. A proper sheet-stock subschema is a future pass.

import { useState } from 'react';
import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { MaterialCost } from '@/shared/crmTypes';

interface LocalState {
  sheetW: string;
  sheetH: string;
  thickness: string;
  qtyPerSheet: string;
  wastePct: string;
}

export function SheetStockStep({ part, onChange }: StepProps) {
  const [material, setMaterial] = useState<string>(
    typeof part.materialCost?.vendorPartNum === 'string' ? part.materialCost.vendorPartNum : '',
  );
  const [local, setLocal] = useState<LocalState>({
    sheetW: '',
    sheetH: '',
    thickness: '',
    qtyPerSheet: '',
    wastePct: '15',
  });

  function persistMaterial(newMaterial: string) {
    setMaterial(newMaterial);
    const next: MaterialCost = {
      ...(part.materialCost ?? {}),
      vendorPartNum: newMaterial,
    };
    onChange({ materialCost: next });
  }

  return (
    <div className="space-y-3">
      <Field label="Sheet material">
        <input
          type="text"
          value={material}
          onChange={(e) => persistMaterial(e.target.value)}
          placeholder='e.g. 1/4" 304 Stainless · 4×8 sheet'
          autoFocus
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Sheet width (in)">
          <NumberInput
            value={local.sheetW === '' ? undefined : Number.parseFloat(local.sheetW)}
            onChange={(v) => setLocal({ ...local, sheetW: v === undefined ? '' : String(v) })}
            step={0.0625}
            numpadStep={0.0625}
            unit="in"
            placeholder="48"
            numpadTitle="Sheet width"
          />
        </Field>
        <Field label="Sheet height (in)">
          <NumberInput
            value={local.sheetH === '' ? undefined : Number.parseFloat(local.sheetH)}
            onChange={(v) => setLocal({ ...local, sheetH: v === undefined ? '' : String(v) })}
            step={0.0625}
            numpadStep={0.0625}
            unit="in"
            placeholder="96"
            numpadTitle="Sheet height"
          />
        </Field>
        <Field label="Thickness (in)">
          <NumberInput
            value={local.thickness === '' ? undefined : Number.parseFloat(local.thickness)}
            onChange={(v) => setLocal({ ...local, thickness: v === undefined ? '' : String(v) })}
            step={0.0625}
            numpadStep={0.0625}
            unit="in"
            placeholder="0.250"
            numpadTitle="Thickness"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Qty per sheet (rough nesting)">
          <NumberInput
            value={local.qtyPerSheet === '' ? undefined : Number.parseFloat(local.qtyPerSheet)}
            onChange={(v) => setLocal({ ...local, qtyPerSheet: v === undefined ? '' : String(v) })}
            step={1}
            placeholder="12"
            numpadTitle="Qty per sheet"
          />
        </Field>
        <Field label="Waste %">
          <NumberInput
            value={local.wastePct === '' ? undefined : Number.parseFloat(local.wastePct)}
            onChange={(v) => setLocal({ ...local, wastePct: v === undefined ? '' : String(v) })}
            step={1}
            unit="%"
            placeholder="15"
            numpadTitle="Waste %"
          />
        </Field>
      </div>

      <div className="text-xs text-text-mute italic">
        Auto-nesting + per-vendor sheet pricing land in a future pass. For now these inputs
        get carried as notes — fill them in for documentation.
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
